import { defineStore } from 'pinia'
import { ref } from 'vue'

export type SupportedLocale = 'ru' | 'en' | 'vn'

export const SUPPORTED_LOCALES: SupportedLocale[] = ['ru', 'en', 'vn']
export const DEFAULT_LOCALE: SupportedLocale = 'ru'

export const useLocaleStore = defineStore('locale', () => {
  const currentLocale = ref<SupportedLocale>(DEFAULT_LOCALE)

  /**
   * Определяет предпочитаемую локаль браузера
   * @returns SupportedLocale или null если локаль браузера не поддерживается
   */
  function detectBrowserLocale(): SupportedLocale | null {
    const browserLang = navigator.language || (navigator as any).userLanguage
    
    if (!browserLang) {
      return null
    }

    // Извлекаем основной язык (например, 'en-US' -> 'en', 'ru-RU' -> 'ru')
    const langCode = browserLang.split('-')[0].toLowerCase()

    // Проверяем поддерживается ли этот язык
    if (SUPPORTED_LOCALES.includes(langCode as SupportedLocale)) {
      return langCode as SupportedLocale
    }

    // Особые случаи маппинга
    // Вьетнамский может быть 'vi' в браузере
    if (langCode === 'vi') {
      return 'vn'
    }

    return null
  }

  /**
   * Устанавливает текущую локаль
   */
  function setLocale(locale: SupportedLocale) {
    if (!SUPPORTED_LOCALES.includes(locale)) {
      console.warn(`Unsupported locale: ${locale}, falling back to ${DEFAULT_LOCALE}`)
      locale = DEFAULT_LOCALE
    }

    currentLocale.value = locale
    localStorage.setItem('locale', locale)
  }

  /**
   * Загружает сохранённую локаль из localStorage
   * Если локаль не сохранена (первый заход), определяет её по настройкам браузера
   */
  function loadSavedLocale() {
    const saved = localStorage.getItem('locale') as SupportedLocale | null
    
    // Приоритет 1: Сохранённая локаль
    if (saved && SUPPORTED_LOCALES.includes(saved)) {
      currentLocale.value = saved
      console.log(`[Locale] Loaded saved locale: ${saved}`)
      return
    }

    // Приоритет 2: Локаль браузера (только при первом заходе)
    const browserLocale = detectBrowserLocale()
    if (browserLocale) {
      currentLocale.value = browserLocale
      // Сохраняем в localStorage, чтобы в следующий раз не определять заново
      localStorage.setItem('locale', browserLocale)
      console.log(`[Locale] Detected browser locale: ${browserLocale}`)
      return
    }

    // Приоритет 3: Дефолтная локаль
    console.log(`[Locale] Using default locale: ${DEFAULT_LOCALE}`)
    currentLocale.value = DEFAULT_LOCALE
  }

  return {
    currentLocale,
    setLocale,
    loadSavedLocale,
    detectBrowserLocale,
  }
})
